/**
 * Placeholders accepted by event name / threshold / notify templates. Mirrors
 * `buildTemplateVars` in coms `evaluateOrgEventAutomationRule.ts` — keep the
 * two lists in sync.
 */
export type TemplatePlaceholder = {
  token: string;
  description: string;
  /** Only meaningful for the tropical Active Hurricanes feed. */
  tropicalOnly?: boolean;
  /** Only resolves when the rule has an AOR intersect condition. */
  aorOnly?: boolean;
};

export const TEMPLATE_PLACEHOLDERS: TemplatePlaceholder[] = [
  { token: 'ruleName', description: 'Name of this automation rule' },
  { token: 'featureName', description: 'Name of the matched feature' },
  { token: 'featureId', description: 'Unique id of the matched feature' },
  { token: 'layerLabel', description: 'Label of the data feed' },
  { token: 'resourceCount', description: 'Resources inside the hazard' },
  {
    token: 'impactedResources',
    description: 'Names of the resources inside the hazard',
  },
  { token: 'stormName', description: 'Storm name', tropicalOnly: true },
  {
    token: 'stormId',
    description: 'Storm id (e.g. AL092024)',
    tropicalOnly: true,
  },
  { token: 'stormType', description: 'TD / TS / HU', tropicalOnly: true },
  {
    token: 'advisoryNum',
    description: 'NHC advisory number',
    tropicalOnly: true,
  },
  {
    token: 'impactedAors',
    description: 'Names of the AORs the hazard overlaps',
    aorOnly: true,
  },
  {
    token: 'impactedAorCount',
    description: 'How many AORs the hazard overlaps',
    aorOnly: true,
  },
];

/**
 * The subset the event description may tag — impact counts and names, including
 * AORs the hazard overlapped.
 */
export const DESCRIPTION_PLACEHOLDER_TOKENS = [
  'resourceCount',
  'impactedResources',
  'impactedAors',
  'impactedAorCount',
] as const;

export const DESCRIPTION_PLACEHOLDERS: TemplatePlaceholder[] =
  TEMPLATE_PLACEHOLDERS.filter((placeholder) =>
    (DESCRIPTION_PLACEHOLDER_TOKENS as readonly string[]).includes(
      placeholder.token
    )
  );

/** Tokens the Event name field can insert. */
export const EVENT_NAME_PLACEHOLDER_TOKENS = [
  'ruleName',
  'featureName',
  'layerLabel',
  'stormName',
  'impactedAors',
] as const;

export const EVENT_NAME_PLACEHOLDERS: TemplatePlaceholder[] =
  TEMPLATE_PLACEHOLDERS.filter((placeholder) =>
    (EVENT_NAME_PLACEHOLDER_TOKENS as readonly string[]).includes(
      placeholder.token
    )
  );

/** Stored default when the rule has no eventName template of its own. */
export const DEFAULT_EVENT_NAME_TEMPLATE =
  '{ruleName} criteria met — {featureName}';

/**
 * Fallback when a rule has no eventName template. Mirrors
 * `buildAutomationEventName` in coms `evaluateConditions.ts`.
 */
export function buildAutomationEventName(
  ruleName: string | undefined,
  featureName?: string | null
): string {
  const base = `${ruleName?.trim() || 'Automation'} criteria met`;
  const feature = featureName?.trim();
  return feature ? `${base} — ${feature}` : base;
}

/**
 * Representative values so the editor can show a resolved example without
 * hitting ArcGIS. These are illustrative, not live feature data.
 */
export function sampleTemplateVars(options: {
  tropical: boolean;
  layerLabel?: string;
}): Record<string, string> {
  const layerLabel = options.layerLabel?.trim() || 'ArcGIS layer';
  const sharedVars = {
    impactedAors: 'Southeast, Gulf',
    impactedAorCount: '2',
    impactedResources: 'Store #1042, Store #1187, Store #1203',
  };
  if (options.tropical) {
    return {
      ruleName: 'Automation',
      featureName: 'Helene',
      featureId: 'AL092024',
      layerLabel,
      resourceCount: '12',
      stormName: 'Helene',
      stormId: 'AL092024',
      stormType: 'HU',
      advisoryNum: '15',
      ...sharedVars,
    };
  }
  return {
    ruleName: 'Automation',
    featureName: 'Zone A',
    featureId: '1042',
    layerLabel,
    resourceCount: '12',
    stormName: 'Zone A',
    stormId: '1042',
    stormType: '',
    advisoryNum: '',
    ...sharedVars,
  };
}

/**
 * Values the Event name example may resolve. Only form-known fields — never a
 * fake feature like Zone A. Unlisted tokens stay visible as `@token`.
 */
export function eventNamePreviewVars(options: {
  ruleName?: string;
  layerLabel?: string;
}): Record<string, string> {
  const vars: Record<string, string> = {};
  const ruleName = options.ruleName?.trim();
  const layerLabel = options.layerLabel?.trim();
  if (ruleName) vars.ruleName = ruleName;
  if (layerLabel) vars.layerLabel = layerLabel;
  return vars;
}

/**
 * Every token the backend can substitute. Templates are stored and sent as
 * `{token}`; the editor only ever shows `@token`. Conversion is gated on this
 * set so a literal at-sign (an email address, a handle) is left alone.
 */
const KNOWN_TOKENS = new Set<string>([
  ...TEMPLATE_PLACEHOLDERS.map((placeholder) => placeholder.token),
  // Supplied ad hoc at notify time rather than by buildTemplateVars.
  'eventName',
  'thresholdDescription',
  'layerHash',
]);

/** Stored `{token}` to the `@token` form shown in the editor. */
export function toDisplayTemplate(template: string | undefined): string {
  if (!template) return '';
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    KNOWN_TOKENS.has(key) ? `@${key}` : match
  );
}

/** Editor `@token` back to the stored `{token}` the backend substitutes. */
export function toStoredTemplate(template: string | undefined): string {
  if (!template) return '';
  return template.replace(/@(\w+)/g, (match, key: string) =>
    KNOWN_TOKENS.has(key) ? `{${key}}` : match
  );
}

/**
 * Substitute known placeholders in either syntax. Unknown tokens are left
 * intact so a typo stays visible in the example rather than silently
 * vanishing.
 */
export function renderTemplatePreview(
  template: string | undefined,
  vars: Record<string, string>
): string {
  if (!template?.trim()) return '';
  return template
    .replace(/\{(\w+)\}/g, (match, key: string) =>
      key in vars ? vars[key] : match
    )
    .replace(/@(\w+)/g, (match, key: string) =>
      key in vars ? vars[key] : match
    );
}

/** Insert `@token` at the cursor, or append when there is no selection. */
export function insertPlaceholder(
  current: string,
  token: string,
  selectionStart?: number | null,
  selectionEnd?: number | null
): { value: string; caret: number } {
  const insert = `@${token}`;
  const start = selectionStart ?? current.length;
  const end = selectionEnd ?? start;
  const value = `${current.slice(0, start)}${insert}${current.slice(end)}`;
  return { value, caret: start + insert.length };
}
